import React, { useState, useEffect } from 'react';
import { fetchDealersGrouped } from '../api';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  .dn-section {
    background: #faf8f4;
    padding: 80px 0 100px;
    font-family: 'DM Sans', sans-serif;
    position: relative;
  }

  .dn-section::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1a3a2a 0%, #c9a84c 50%, #1a3a2a 100%);
  }

  .dn-header {
    text-align: center;
    margin-bottom: 64px;
    padding: 0 24px;
  }

  .dn-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #c9a84c;
    margin-bottom: 16px;
  }

  .dn-eyebrow::before,
  .dn-eyebrow::after {
    content: '';
    display: block;
    width: 32px;
    height: 1px;
    background: #c9a84c;
  }

  .dn-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px, 5vw, 54px);
    font-weight: 600;
    color: #1a3a2a;
    line-height: 1.1;
    margin: 0 0 16px;
    letter-spacing: -0.01em;
  }

  .dn-subtitle {
    font-size: 15px;
    font-weight: 300;
    color: #6b7c6b;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.6;
  }

  .dn-stats {
    display: flex;
    justify-content: center;
    gap: 48px;
    margin-top: 40px;
    padding-top: 32px;
    border-top: 1px solid #e2ddd5;
  }

  .dn-stat {
    text-align: center;
  }

  .dn-stat-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 32px;
    font-weight: 700;
    color: #1a3a2a;
    line-height: 1;
  }

  .dn-stat-label {
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9aaa9a;
    margin-top: 4px;
  }

  .dn-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .dn-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 2px;
    background: #e2ddd5;
    border: 2px solid #e2ddd5;
  }

  .dn-card {
    background: #faf8f4;
    padding: 32px;
    transition: background 0.2s ease;
    position: relative;
    overflow: hidden;
  }

  .dn-card::after {
    content: '';
    position: absolute;
    bottom: 0; left: 32px; right: 32px;
    height: 0;
    background: #1a3a2a;
    transition: height 0.25s ease;
  }

  .dn-card:hover {
    background: #fff;
    z-index: 1;
  }

  .dn-card:hover::after {
    height: 3px;
  }

  .dn-city-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e2ddd5;
  }

  .dn-city-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px;
    font-weight: 700;
    color: #1a3a2a;
    letter-spacing: 0.04em;
    line-height: 1.2;
  }

  .dn-city-badge {
    background: #1a3a2a;
    color: #c9a84c;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    padding: 3px 8px;
    border-radius: 2px;
    white-space: nowrap;
    margin-top: 2px;
  }

  .dn-dealers {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .dn-dealer {
    padding: 10px 0;
    border-bottom: 1px dashed #e2ddd5;
  }

  .dn-dealer:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .dn-dealer-name {
    font-size: 13.5px;
    font-weight: 500;
    color: #2c2c2c;
    line-height: 1.3;
    margin-bottom: 4px;
  }

  .dn-dealer-address {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 300;
    color: #5a6c5a;
    line-height: 1.4;
    margin-bottom: 4px;
  }

  .dn-address-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: #c9a84c;
  }

  .dn-dealer-contact {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    font-weight: 300;
    color: #7a8c7a;
    line-height: 1.5;
  }

  .dn-phone-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: #c9a84c;
  }

  @media (max-width: 640px) {
    .dn-grid {
      grid-template-columns: 1fr;
    }
    .dn-stats {
      gap: 24px;
    }
    .dn-container {
      padding: 0 16px;
    }
  }
`;

export default function DealersNetwork() {
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchDealersGrouped()
            .then(apiData => {
                if (Array.isArray(apiData) && apiData.length > 0) {
                    setData(apiData);
                }
            })
            .catch(() => {
                // Silently fall back to hardcoded data
            });
    }, []);

    const totalDealers = data.reduce((sum, c) => sum + c.dealers.length, 0);

    return (
        <>
            <style>{styles}</style>
            <section className="dn-section">
                <div className="dn-header">
                    <div className="dn-eyebrow">Authorised Distribution</div>
                    <h2 className="dn-title">Our Dealers Network</h2>
                    <p className="dn-subtitle">
                        Trusted legal booksellers across India — find an authorised dealer near you.
                    </p>
                    <div className="dn-stats">
                        <div className="dn-stat">
                            <div className="dn-stat-num">{data.length}</div>
                            <div className="dn-stat-label">Cities</div>
                        </div>
                        <div className="dn-stat" style={{ borderLeft: '1px solid #e2ddd5', paddingLeft: 48 }}>
                            <div className="dn-stat-num">{totalDealers}</div>
                            <div className="dn-stat-label">Dealers</div>
                        </div>
                        <div className="dn-stat" style={{ borderLeft: '1px solid #e2ddd5', paddingLeft: 48 }}>
                            <div className="dn-stat-num">All India</div>
                            <div className="dn-stat-label">Coverage</div>
                        </div>
                    </div>
                </div>

                <div className="dn-container">
                    <div className="dn-grid">
                        {data.map((cityData, index) => (
                            <div key={index} className="dn-card">
                                <div className="dn-city-row">
                                    <div className="dn-city-name">{cityData.city}</div>
                                    <div className="dn-city-badge">
                                        {cityData.dealers.length} {cityData.dealers.length === 1 ? 'dealer' : 'dealers'}
                                    </div>
                                </div>
                                <div className="dn-dealers">
                                    {cityData.dealers.map((dealer, dIndex) => (
                                        <div key={dIndex} className="dn-dealer">
                                            <div className="dn-dealer-name">{dealer.name}</div>
                                            {dealer.address && (
                                                <div className="dn-dealer-address">
                                                    <svg className="dn-address-icon" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                        <circle cx="12" cy="10" r="3" />
                                                    </svg>
                                                    {dealer.address}
                                                </div>
                                            )}
                                            <div className="dn-dealer-contact">
                                                <svg className="dn-phone-icon" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.38 2 2 0 0 1 3 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                                {dealer.contact}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}